<template>
  <div class="vc-quantity-wrapper">
    <div class="prepend-container">
      <slot name="prepend" />
    </div>
    <VcInput
      v-model="quantity"
      :name="name"
      :readonly="readonly"
      :disabled="disabled"
      :error="error || (!!errorMessage && (quantity !== 0 || props.minQuantity > 0))"
      :message="quantity === 0 && props.minQuantity === 0 ? '' : errorMessage"
      :aria-label="$t('common.labels.product_quantity')"
      single-line-message
      class="vc-quantity"
      type="number"
      center
      truncate
      @keydown="onKeydown"
      @keyup.enter="changeQuantity"
      @input="onQuantityChanged"
      @blur="onFocusOut"
    >
      <!-- <template #prepend>
        <slot name="prepend" />
      </template>

      <template #append>
        <slot name="append" />
      </template> -->
    </VcInput>
    <div class="append-container">
      <slot name="append" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { toTypedSchema } from "@vee-validate/yup";
import { useField } from "vee-validate";
import { computed, ref, toRefs, watchEffect } from "vue";
import { LINE_ITEM_QUANTITY_LIMIT } from "@/core/constants";
import { useQuantityValidationSchema } from "@/ui-kit/composables";

interface IEmits {
  (event: "update:modelValue", value: number): void;
}

interface IProps {
  name?: string;
  minQuantity?: number;
  maxQuantity?: number;
  disabled?: boolean;
  readonly?: boolean;
  error?: boolean;
  modelValue?: number;
  timeout?: number;
  allowZeroQuantity?: boolean;
}

const emit = defineEmits<IEmits>();
const props = withDefaults(defineProps<IProps>(), {
  timeout: 900,
  minQuantity: 1, // Default min quantity set to 1
});

let timeoutIdOfQuantityChange: number;

const quantity = ref<number | undefined>();

const { minQuantity, maxQuantity, allowZeroQuantity } = toRefs(props);

const { quantitySchema } = useQuantityValidationSchema({ minQuantity, maxQuantity, allowZeroQuantity });

const rules = computed(() => toTypedSchema(quantitySchema.value));

const { errorMessage, setValue, validate } = useField("quantity", rules);

function increaseQuantity() {
  if (quantity.value !== undefined) {
    quantity.value++;
    changeQuantity();
  }
}

function decreaseQuantity() {
  if (quantity.value !== undefined && quantity.value > (props.minQuantity || 0)) {
    quantity.value--;
    changeQuantity();
  }
}

function changeQuantity() {
  clearTimeout(timeoutIdOfQuantityChange);

  // This is where the fix needs to be: don't validate quantity but emit the update directly
  // Allow any numeric value to be emitted when quantity is changed via buttons
  if (quantity.value !== undefined && !isNaN(Number(quantity.value))) {
    emit("update:modelValue", Number(quantity.value));
    return;
  }

  // For manual input, use the existing validation logic
  if (!isQuantity(quantity.value) || quantity.value === props.modelValue) {
    return;
  }

  emit("update:modelValue", quantity.value);
}

async function onQuantityChanged(): Promise<void> {
  // Skip validation when quantity is 0 and minQuantity is 0
  if (quantity.value === 0 && props.minQuantity === 0) {
    emit("update:modelValue", 0);
    return;
  }

  if (!isQuantity(quantity.value)) {
    return;
  }

  setValue(quantity.value);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { valid } = await validate();

  // Even if not valid, we still want to update the UI with whatever the user typed
  // This allows for intermediate states during typing

  clearTimeout(timeoutIdOfQuantityChange);
  timeoutIdOfQuantityChange = +setTimeout(changeQuantity, props.timeout);
}

function onFocusOut() {
  // Allow zero if minQuantity is 0
  if (quantity.value === 0 && props.minQuantity === 0) {
    emit("update:modelValue", 0);
    return;
  }

  if (!isQuantity(quantity.value)) {
    quantity.value = props.modelValue;
  }
}

function onKeydown(e: KeyboardEvent) {
  if (e.key.toLowerCase() === "e" || e.key === "-" || e.key === "." || e.key === ",") {
    e.preventDefault();
  }
}

function isQuantity(qty: unknown): qty is number {
  const qtyAsNumber = Number(quantity.value);

  // Allow zero value if minQuantity is set to 0
  if (qtyAsNumber === 0 && props.minQuantity === 0) {
    return true;
  }

  // Otherwise use standard validation
  return (
    !isNaN(qtyAsNumber) && Number(qtyAsNumber) >= props.minQuantity && Number(qtyAsNumber) <= LINE_ITEM_QUANTITY_LIMIT
  );
}

watchEffect(() => {
  quantity.value = props.modelValue;
});

// Expose the increment/decrement functions to parent components
defineExpose({
  increaseQuantity,
  decreaseQuantity,
});
</script>

<style lang="scss">
.vc-quantity {
  @apply flex-none;
}
</style>
